import classNames from 'classnames';
// @ts-expect-error
import t from '../helpers/calculator_translations';
import type { CalculatorConfig } from './CalculatorConfigGroup';
import { DisciplineSelector } from './DisciplineSelector';
import { LanguageSelector } from './LanguageSelector';

interface CalculatorFormProps {
    locale: string;
    calculatorConfig: CalculatorConfig;
    words: number | null;
    setWords: (value: number) => void;
    sourceLanguage: string;
    setSourceLanguage: (value: string) => void;
    targetLanguage: string;
    setTargetLanguage: (value: string) => void;
    discipline: number | null;
    setDiscipline: (value: number) => void;
    formHasChanges: boolean;
    submit: () => void;
}
export const CalculatorForm = ({
    locale,
    words,
    setWords,
    sourceLanguage,
    setSourceLanguage,
    targetLanguage,
    setTargetLanguage,
    discipline,
    setDiscipline,
    submit,
}: CalculatorFormProps): JSX.Element => {
    const swapLanguages = () => {
        const originalSourceLanguage = sourceLanguage;

        setSourceLanguage(targetLanguage);
        setTargetLanguage(originalSourceLanguage);
    };

    return (
        <form
            className={'col-12 d-flex justify-content-between flex-wrap flex-xl-nowrap'}
            onSubmit={(e) => {
                e.preventDefault();
                submit();
            }}
        >
            <div className={'search-term'}>
                {/* biome-ignore lint/a11y/noLabelWithoutControl: label for child component select */}
                <label className={'sublabel'}>{t('calculator.language_combination')}</label>
                <div className={'d-flex subcontent align-items-baseline'}>
                    <div className={'d-flex me-2 flex-grow-1'}>
                        <LanguageSelector
                            locale={locale}
                            name={'source_language'}
                            action={setSourceLanguage}
                            value={sourceLanguage}
                        />
                    </div>
                    <button
                        type="button"
                        className={'me-2 btn btn-link p-0 border-0'}
                        onClick={swapLanguages}
                        aria-label={t('calculator.swap_languages') || 'Swap languages'}
                    >
                        <svg
                            aria-hidden="true"
                            style={{ height: '14px', width: '14px' }}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                        >
                            <path d="M406.6 374.6l96-96c12.5-12.5 12.5-32.8 0-45.3l-96-96c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224l-293.5 0 41.4-41.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-96 96c-12.5 12.5-12.5 32.8 0 45.3l96 96c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 288l293.5 0-41.4 41.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0z" />
                        </svg>
                    </button>
                    <div className={'d-flex flex-grow-1'}>
                        <LanguageSelector
                            locale={locale}
                            name="target_language"
                            action={setTargetLanguage}
                            value={targetLanguage}
                        />
                    </div>
                </div>
            </div>

            <div className={'search-term'}>
                {/* biome-ignore lint/a11y/noLabelWithoutControl: label for child component select */}
                <label className={'sublabel'}>{t('calculator.disciplines')}</label>
                <div className={'subcontent'}>
                    <DisciplineSelector discipline={discipline} action={setDiscipline} />
                </div>
            </div>

            <div className={'search-term'}>
                {/* biome-ignore lint/a11y/noLabelWithoutControl: label for child component input */}
                <label className={'sublabel'}>{t('calculator.word_count')}</label>
                <div className={'subcontent'}>
                    <input
                        className={'form-control'}
                        type="number"
                        inputMode="numeric"
                        // 1, not 0: there is no price for nothing, and Connect
                        // answers a request for zero words with a redirect rather
                        // than a price, which reaches the visitor as a failure.
                        // `required` only rejects an empty field; zero is a value.
                        min="1"
                        pattern="[0-9]*"
                        style={{ height: 'auto' }}
                        onChange={(e) => {
                            setWords(parseInt(e.target.value, 10));
                        }}
                        required={true}
                        value={words ?? ''}
                        placeholder={t('calculator.word_count.placeholder')}
                    />
                </div>
            </div>

            <div className={'search-term justify-content-sm-end align-items-end'}>
                <button
                    className={classNames('btn btn-lg btn-tertiary')}
                    style={{ zIndex: 0 }}
                    type="submit"
                >
                    {t('calculator.buttons.calculate')}
                </button>
            </div>
        </form>
    );
};
